import type { CSSProperties, Ref } from 'react';
import {
  LEVEL_PANORAMA_TILE_COUNT,
  type PanoramaTiles,
} from './levelVisualAssets';

type LevelPanoramaTilesProps = {
  tiles: PanoramaTiles;
  artWidth: number;
  artHeight: number;
  translateX: number;
  displayScale: number;
  canvasTopPx: number;
  mode: 'bg' | 'fg';
  zIndex: number;
  /** RAF owns transform after mount; ref targets the camera wrapper. */
  layerRef?: Ref<HTMLDivElement>;
};

/**
 * One camera transform wrapper; all tiles laid out in art space inside it.
 * All tiles stay mounted so imperative camera scroll does not need React remounts.
 */
export function LevelPanoramaTiles({
  tiles,
  artWidth,
  artHeight,
  translateX,
  displayScale,
  canvasTopPx,
  mode,
  zIndex,
  layerRef,
}: LevelPanoramaTilesProps) {
  const tileArtWidth = artWidth / LEVEL_PANORAMA_TILE_COUNT;
  const indices = Array.from({ length: LEVEL_PANORAMA_TILE_COUNT }, (_, i) => i);

  // transform is applied imperatively (RAF / playfieldMotionBindings) so React
  // re-renders do not reset camera mid-run. Initial pose uses translateX prop
  // only as a seed via applyPlayfieldCamera.
  void translateX;
  const layerStyle: CSSProperties =
    mode === 'bg'
      ? {
          zIndex,
          position: 'absolute',
          left: 0,
          top: 0,
          width: artWidth,
          height: artHeight,
          transformOrigin: '0 0',
          pointerEvents: 'none',
        }
      : {
          zIndex,
          position: 'absolute',
          left: 0,
          bottom: canvasTopPx,
          width: artWidth,
          transformOrigin: 'left bottom',
          pointerEvents: 'none',
        };

  return (
    <div
      ref={layerRef}
      className={`level-playfield__panorama level-playfield__panorama--${mode}`}
      style={layerStyle}
    >
      {indices.map((index) => {
        const src = tiles[index];
        const leftArt = index * tileArtWidth;
        if (mode === 'bg') {
          return (
            <img
              key={`bg-${index}`}
              className="level-playfield__bg-tile"
              src={src}
              alt=""
              draggable={false}
              style={{
                left: leftArt,
                width: tileArtWidth,
                height: artHeight,
              }}
            />
          );
        }
        return (
          <img
            key={`fg-${index}`}
            className="level-playfield__fg-tile"
            src={src}
            alt=""
            draggable={false}
            style={{
              left: leftArt,
              width: tileArtWidth,
            }}
          />
        );
      })}
    </div>
  );
}
